import { useEffect, useState } from "react";
import {
  getCars,
  getCar,
  approveCar,
  disapproveCar,
  deleteCar,
} from "../services/car.service";
import CarDetailsModal from "../components/car/CarDetailsModal";
import CarTable from "../components/car/CarTable";
import CarHeader from "../components/car/CarHeader";
import CarCreateModal from "../components/car/CarCreateModal";
import CarDeleteModal from "../components/car/CarDeleteModal";
import CarEditModal from "../components/car/CarEditModal";
import { useToast } from "../context/ToastContext";

export default function Cars() {
  const { showToast } = useToast();
  const [data, setData] = useState({ data: [], last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [approveFilter, setApproveFilter] = useState("all");

  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadCars();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [page, search, approveFilter]);

  async function loadCars() {
    setLoading(true);
    try {
      const params = {
        page,
        search,
        approve: approveFilter,
      };
      const res = await getCars(params);
      if (res && res.data) {
        setData({
          data: res.data,
          last_page: res.last_page,
          total: res.total,
        });
      } else if (Array.isArray(res)) {
        setData({ data: res, last_page: 1, total: res.length });
      }
    } catch (err) {
      showToast("Erreur de chargement", "error");
      setData({ data: [], last_page: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(payload) {
    try {
      showToast("Véhicule créé avec succès", "success");
      setOpenCreate(false);
      setPage(1);
      loadCars();
    } catch (err) {
      showToast("Erreur lors de la création", "error");
    }
  }

  async function handleEditSubmit(id, payload) {
    try {
      showToast("Véhicule modifié avec succès", "success");
      setEditTarget(null);
      loadCars();
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la modification", "error");
    }
  }

  async function handleToggleApprove(car) {
    const previousStatus = car.isApproved;
    const newStatus = !previousStatus;

    setData((prev) => ({
      ...prev,
      data: prev.data.map((c) =>
        c.id === car.id ? { ...c, isApproved: newStatus } : c,
      ),
    }));

    try {
      if (previousStatus) {
        await disapproveCar(car.id);
        showToast("Véhicule désapprouvé", "success");
      } else {
        await approveCar(car.id);
        showToast("Véhicule approuvé", "success");
      }
      loadCars();
    } catch (err) {
      console.error(err);
      setData((prev) => ({
        ...prev,
        data: prev.data.map((c) =>
          c.id === car.id ? { ...c, isApproved: previousStatus } : c,
        ),
      }));
      showToast("Erreur lors de la modification du statut", "error");
    }
  }
  async function handleView(car) {
    try {
      const fullCar = await getCar(car.id);
      setSelected(fullCar);
    } catch (err) {
      showToast("Impossible de charger les détails", "error");
    }
  }
  async function handleDelete(id) {
    try {
      await deleteCar(id);
      showToast("Véhicule supprimé", "success");
      setDeleteTarget(null);
      loadCars();
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  }
  if (loading && data.data.length === 0) {
    return <div className="loaderContainer">Chargement des véhicules...</div>;
  }
  return (
    <div className="page">
      <CarHeader
        total={data.total}
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(1);
        }}
        approveFilter={approveFilter}
        onApproveChange={(v) => {
          setApproveFilter(v);
          setPage(1);
        }}
        onAdd={() => setOpenCreate(true)}
      />
      <div className={loading ? "tableContainer loading" : "tableContainer"}>
        <CarTable
          cars={data.data}
          onView={handleView}
          onEdit={setEditTarget}
          onDelete={(car) => setDeleteTarget(car)}
          onToggleApprove={handleToggleApprove}
        />
      </div>
      {/* Pagination */}
      <div className="paginationContainer">
        <div className="pagiInfo">
          Page <strong>{page}</strong> sur {data.last_page}
        </div>
        <div className="pagiControls">
          <button
            className="pagiBtn"
            disabled={page === 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Précédent
          </button>
          <button
            className="pagiBtn"
            disabled={page === data.last_page || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant
          </button>
        </div>
      </div>

      <CarCreateModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSubmit={handleCreate}
      />
      <CarEditModal
        open={!!editTarget}
        car={editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEditSubmit}
      />
      {selected && (
        <CarDetailsModal car={selected} onClose={() => setSelected(null)} />
      )}

      {deleteTarget && (
        <CarDeleteModal
          car={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget.id)}
        />
      )}
    </div>
  );
}
